/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * MFAVerifyPage Component
 * ============================================================================
 *
 * MFA verification page for login flow.
 * Supports TOTP and Email OTP backup method.
 *
 * ============================================================================
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, AlertCircle, ArrowLeft, Mail, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { CodeInput } from '@/components/ui/CodeInput';
import { Button } from '@/components/ui/Button';
import { useMfaVerify, useSendEmailOtp } from '@/hooks/api/useAuth';
import { useDeviceFingerprint, getDeviceFingerprintSync } from '@/hooks/useDeviceFingerprint';
import { getErrorMessage, isApiError } from '@/services/api/errors';
import { MfaMethod } from '@/types/auth.types';

interface LocationState {
  mfaToken: string;
  mfaMethods?: MfaMethod[];
}

export function MFAVerifyPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const { fingerprint } = useDeviceFingerprint();
  const mfaVerifyMutation = useMfaVerify();
  const sendEmailOtpMutation = useSendEmailOtp();

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<MfaMethod>('TOTP');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Available MFA methods from login response
  const availableMethods = state?.mfaMethods || ['TOTP'];
  const hasMultipleMethods = availableMethods.length > 1;

  // Redirect if no MFA token
  useEffect(() => {
    if (!state?.mfaToken) {
      navigate('/auth/login');
    }
  }, [state, navigate]);

  // Set initial method based on available methods
  useEffect(() => {
    if (availableMethods.length > 0 && !availableMethods.includes(selectedMethod)) {
      setSelectedMethod(availableMethods[0]);
    }
  }, [availableMethods, selectedMethod]);

  // Start cooldown timer on initial page load for EMAIL_OTP (code was already sent during login)
  useEffect(() => {
    // Only set initial cooldown if EMAIL_OTP is available
    if (availableMethods.includes('EMAIL_OTP')) {
      setResendCooldown(60); // Start with 60 second cooldown
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency - only run once on mount

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendEmailOtp = () => {
    if (!state?.mfaToken || sendEmailOtpMutation.isPending) return;

    // If cooldown is active, show a friendly message instead of error
    if (resendCooldown > 0) {
      setError(t('auth.mfa.waitSeconds').replace('{{seconds}}', String(resendCooldown)));
      return;
    }

    setError(null);
    setResendSuccess(false);
    sendEmailOtpMutation.mutate({ mfaToken: state.mfaToken, isSetup: false }, {
      onSuccess: () => {
        setResendCooldown(60); // 60 second cooldown
        setResendSuccess(true);
        // Clear success message after 5 seconds
        setTimeout(() => setResendSuccess(false), 5000);
      },
      onError: (err) => {
        if (isApiError(err)) {
          // Check for rate limiting errors
          if (err.code === 'RATE_LIMITED' || err.message?.toLowerCase().includes('wait') || err.message?.toLowerCase().includes('too many')) {
            setError(err.message || t('auth.mfa.waitSeconds').replace('{{seconds}}', '60'));
          } else {
            setError(err.message || t('notifications.error'));
          }
        } else {
          setError(t('notifications.error'));
        }
      },
    });
  };

  const handleVerify = async (verificationCode: string) => {
    if (!state?.mfaToken) return;

    setError(null);

    // Get fingerprint synchronously to ensure device info is always available
    const deviceFp = fingerprint ?? getDeviceFingerprintSync();

    mfaVerifyMutation.mutate(
      {
        mfaToken: state.mfaToken,
        code: verificationCode,
        method: selectedMethod,
        trustDevice: false,
        deviceFingerprint: deviceFp.hash,
        // Include device info for login alert
        browser: deviceFp.info.browser,
        browserVersion: deviceFp.info.browserVersion,
        os: deviceFp.info.os,
        osVersion: deviceFp.info.osVersion,
      },
      {
        onError: (err) => {
          if (isApiError(err)) {
            setError(err.message || getErrorMessage(err.code));
          } else {
            setError(t('auth.mfa.invalidCode'));
          }
          setCode('');
        },
      }
    );
  };

  const handleMethodChange = (method: MfaMethod) => {
    setSelectedMethod(method);
    setCode('');
    setError(null);
    setResendSuccess(false);
    // Don't reset cooldown here - let the useEffect handle it for EMAIL_OTP
  };

  const handleCodeComplete = (completeCode: string) => {
    setCode(completeCode);
    handleVerify(completeCode);
  };

  const getSubtitle = () => {
    if (selectedMethod === 'EMAIL_OTP') {
      return t('auth.mfa.subtitleEmail');
    }
    return t('auth.mfa.subtitle');
  };

  return (
    <AuthLayout
      title={t('auth.mfa.title')}
      subtitle={getSubtitle()}
      icon={<Shield size={28} />}
    >
      <div className="mfa-verify">
        {/* Error message */}
        {error && (
          <div className="mfa-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Method selector */}
        {hasMultipleMethods && (
          <div className="mfa-method-selector">
            {availableMethods.includes('TOTP') && (
              <button
                type="button"
                className={`mfa-method-btn ${selectedMethod === 'TOTP' ? 'active' : ''}`}
                onClick={() => handleMethodChange('TOTP')}
              >
                <Smartphone size={18} />
                <span>{t('auth.mfa.authenticatorApp')}</span>
              </button>
            )}
            {availableMethods.includes('EMAIL_OTP') && (
              <button
                type="button"
                className={`mfa-method-btn ${selectedMethod === 'EMAIL_OTP' ? 'active' : ''}`}
                onClick={() => handleMethodChange('EMAIL_OTP')}
              >
                <Mail size={18} />
                <span>{t('auth.mfa.emailCode')}</span>
              </button>
            )}
          </div>
        )}

        {/* Code input */}
        <div className="mfa-code-section">
          <CodeInput
            length={6}
            onComplete={handleCodeComplete}
            onChange={setCode}
            disabled={mfaVerifyMutation.isPending}
            error={!!error}
          />
        </div>

        {/* Verify button */}
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          loading={mfaVerifyMutation.isPending}
          loadingText={t('auth.mfa.verifying')}
          disabled={code.length !== 6}
          onClick={() => handleVerify(code)}
          glow
        >
          {t('auth.mfa.verifyButton')}
        </Button>

        {/* Resend email OTP */}
        {selectedMethod === 'EMAIL_OTP' && (
          <div className="mfa-resend-section">
            {resendSuccess && (
              <div className="mfa-resend-success">
                <Mail size={16} />
                <span>{t('auth.mfa.codeSent')}</span>
              </div>
            )}
            <div className="mfa-resend-container">
              <span className="mfa-resend-text">{t('auth.mfa.didntReceive')}</span>
              <button
                type="button"
                className={`mfa-resend-btn ${resendCooldown > 0 ? 'mfa-resend-btn-disabled' : ''}`}
                onClick={handleResendEmailOtp}
                disabled={sendEmailOtpMutation.isPending}
              >
                <Mail size={14} />
                {sendEmailOtpMutation.isPending
                  ? t('auth.mfa.sending')
                  : resendCooldown > 0
                  ? t('auth.mfa.waitSeconds').replace('{{seconds}}', String(resendCooldown))
                  : t('auth.mfa.resendCode')}
              </button>
            </div>
          </div>
        )}

        {/* Back to login */}
        <div className="mfa-footer">
          <button
            type="button"
            className="mfa-back-link"
            onClick={() => navigate('/auth/login')}
          >
            <ArrowLeft size={16} />
            {t('auth.mfa.backToLogin')}
          </button>
        </div>
      </div>

      <style>{`
        .mfa-verify {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .mfa-error {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: var(--color-error-light);
          border: 1px solid var(--color-error);
          border-radius: var(--radius-lg);
          color: var(--color-error);
          font-size: var(--text-sm);
          animation: shake 0.3s ease-in-out;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }

        .mfa-method-selector {
          display: flex;
          gap: 12px;
        }

        .mfa-method-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-lg);
          color: var(--color-text-muted);
          font-size: var(--text-sm);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .mfa-method-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .mfa-method-btn.active {
          background: rgba(163, 22, 33, 0.15);
          border-color: var(--color-brand-primary);
          color: var(--color-brand-primary);
        }

        [data-theme="light"] .mfa-method-btn {
          background: rgba(0, 0, 0, 0.03);
          border-color: rgba(0, 0, 0, 0.1);
        }

        [data-theme="light"] .mfa-method-btn:hover {
          background: rgba(0, 0, 0, 0.06);
          border-color: rgba(0, 0, 0, 0.15);
        }

        [data-theme="light"] .mfa-method-btn.active {
          background: rgba(26, 95, 122, 0.1);
          border-color: var(--color-brand-primary);
          color: var(--color-brand-primary);
        }

        .mfa-code-section {
          padding: 8px 0;
        }

        .mfa-resend-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .mfa-resend-success {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          background: rgba(16, 185, 129, 0.15);
          border: 1px solid rgba(16, 185, 129, 0.4);
          border-radius: var(--radius-lg);
          color: #10B981;
          font-size: var(--text-sm);
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .mfa-resend-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          font-size: var(--text-sm);
        }

        .mfa-resend-text {
          color: var(--color-text-muted);
        }

        .mfa-resend-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: rgba(163, 22, 33, 0.15);
          border: 1px solid rgba(163, 22, 33, 0.4);
          border-radius: var(--radius-md);
          font-size: var(--text-sm);
          font-weight: 500;
          color: var(--color-brand-primary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .mfa-resend-btn:hover:not(:disabled):not(.mfa-resend-btn-disabled) {
          background: rgba(163, 22, 33, 0.25);
          border-color: rgba(163, 22, 33, 0.6);
          transform: translateY(-1px);
        }

        .mfa-resend-btn:disabled,
        .mfa-resend-btn-disabled {
          background: rgba(100, 116, 139, 0.1);
          border-color: rgba(100, 116, 139, 0.2);
          color: var(--color-text-muted);
          cursor: not-allowed;
          transform: none;
        }

        [data-theme="light"] .mfa-resend-btn {
          background: rgba(26, 95, 122, 0.1);
          border-color: rgba(26, 95, 122, 0.3);
        }

        [data-theme="light"] .mfa-resend-btn:hover:not(:disabled):not(.mfa-resend-btn-disabled) {
          background: rgba(26, 95, 122, 0.15);
          border-color: rgba(26, 95, 122, 0.5);
        }

        .mfa-footer {
          display: flex;
          justify-content: center;
          padding-top: 8px;
        }

        .mfa-back-link {
          display: flex;
          align-items: center;
          gap: 6px;
          background: transparent;
          border: none;
          font-size: var(--text-sm);
          color: var(--color-text-muted);
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .mfa-back-link:hover {
          color: var(--color-text-primary);
        }

        @media (max-width: 640px) {
          .mfa-verify {
            gap: 1.25rem;
          }
          .mfa-method-selector {
            flex-wrap: wrap;
          }
          .mfa-resend-container {
            flex-direction: column;
            gap: 8px;
            align-items: center;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .mfa-error { animation: none !important; }
          .mfa-resend-success { animation: none !important; }
        }
      `}</style>
    </AuthLayout>
  );
}

export default MFAVerifyPage;
